import { ORBI_APPOINTMENT_MAP_PREVIEW } from '@patient-ui/shared/constants';
import { OrbiAppointment } from '@patient-ui/shared/models';

/**
 * Pure map helpers shared by the Orbi cards that draw a lab location — the
 * Upcoming Appointment card and the Visit History card.
 *
 * Both render the same `OrbiAppointment` row (mobile passes
 * `UpcomingAppointmentData` to both and shares ONE `MapPreviewTile` between
 * them), so the interactive link, the static tile and their availability gates
 * are one behavior, not two. They lived as six identical members on each
 * component; extracted here on review (PR 3170) so the geometry, the key
 * handling and the coordinates-else-address preference cannot drift apart.
 *
 * Pure and DI-free: the Maps key is passed in rather than injected, because
 * `EnvironmentService` lives in the app layer and shared-utils must not depend
 * on it (Nx module boundaries). Callers pass
 * `environmentService.googleMapsKey`.
 */

/** Google Static Maps endpoint backing the preview tile. */
const STATIC_MAP_URL = 'https://maps.googleapis.com/maps/api/staticmap';

/**
 * The location as a Maps query: `lat,lng` when the row carried coordinates, else
 * the formatted address, else ''. Shared by the interactive link and the static
 * preview so both point at the same place.
 */
function orbiMapQuery(appointment: OrbiAppointment): string {
  if (appointment.latitude != null && appointment.longitude != null) {
    return `${appointment.latitude},${appointment.longitude}`;
  }
  return appointment.locationAddress ?? '';
}

/**
 * Google Maps link for the row's location — the portal's existing `?q=` idiom
 * (`review-appointment-details` / `find-a-lab-results` build the same URL).
 * Prefers coordinates when the row carried them, else the formatted address.
 * Empty when neither is available, so the address renders as plain text instead
 * of a dead link.
 */
export function orbiMapUrl(appointment: OrbiAppointment): string {
  if (appointment.latitude != null && appointment.longitude != null) {
    return `https://maps.google.com/?q=${appointment.latitude},${appointment.longitude}`;
  }
  if (appointment.locationAddress) {
    return `https://maps.google.com/?q=${encodeURIComponent(
      appointment.locationAddress
    )}`;
  }
  return '';
}

/**
 * True when the row has somewhere the user can actually be sent — coordinates or
 * an address. Mirrors mobile's `hasMappableDestination`, which gates whether the
 * location renders as an underlined link or as plain text.
 */
export function orbiHasMappableDestination(
  appointment: OrbiAppointment
): boolean {
  return orbiMapUrl(appointment).length > 0;
}

/**
 * Google Static Maps image for the row's location (AC 3.4). Centers on
 * coordinates when the row carried them, else on the formatted address — Google
 * geocodes the `center`/`markers` strings server-side, so an address-only row
 * still gets a real map.
 *
 * Returns '' when there is nothing to place OR no Maps key is configured, and
 * the template then omits the preview entirely rather than requesting an image
 * that would come back as Google's "sorry" tile.
 *
 * `encodedGoogleMapsKey` is base64-encoded in the environment file and decoded
 * with `atob` here, exactly as `GoogleMapsApiService` does for geocoding.
 */
export function orbiMapPreviewUrl(
  appointment: OrbiAppointment,
  encodedGoogleMapsKey: string | undefined
): string {
  const center = orbiMapQuery(appointment);
  if (!center) {
    return '';
  }
  if (!encodedGoogleMapsKey) {
    return '';
  }

  const url = new URL(STATIC_MAP_URL);
  url.searchParams.set('center', center);
  url.searchParams.set('zoom', String(ORBI_APPOINTMENT_MAP_PREVIEW.zoom));
  url.searchParams.set(
    'size',
    `${ORBI_APPOINTMENT_MAP_PREVIEW.width}x${ORBI_APPOINTMENT_MAP_PREVIEW.height}`
  );
  url.searchParams.set('scale', String(ORBI_APPOINTMENT_MAP_PREVIEW.scale));
  url.searchParams.set(
    'markers',
    `color:${ORBI_APPOINTMENT_MAP_PREVIEW.markerColor}|${center}`
  );
  url.searchParams.set('key', atob(encodedGoogleMapsKey));
  return url.toString();
}

/** True when a map preview can be built for this row. */
export function orbiHasMapPreview(
  appointment: OrbiAppointment,
  encodedGoogleMapsKey: string | undefined
): boolean {
  return orbiMapPreviewUrl(appointment, encodedGoogleMapsKey).length > 0;
}
